/**
 * Id scheme (doc 04 · Data model, CLAUDE.md rule 4).
 *
 * monday uses `bigint` ids for account-level entities and *string* ids for columns and groups,
 * scoped to a board. We keep exactly that. Because a JS `number` cannot safely carry every
 * bigint and `JSON.stringify` refuses `BigInt`, every account-level id crosses a wire (tRPC,
 * GraphQL, Socket.IO) as a **decimal string**. The database stores them as `bigint`.
 */
import { z } from 'zod';
const DECIMAL_ID = /^\d{1,19}$/;
/**
 * `create_column` documents `id` as 1–20 characters, lowercase letters and underscores.
 * Real boards also contain digits (`date4`, `text_mkr1`), so digits are allowed.
 */
const BOARD_SCOPED_ID = /^[a-z0-9_]{1,20}$/;
export function normalizeId(value) {
    return typeof value === 'string' ? value.trim() : value.toString();
}
export function isBigIntId(value) {
    return ((typeof value === 'string' || typeof value === 'number' || typeof value === 'bigint') &&
        DECIMAL_ID.test(normalizeId(value)));
}
/** Zod schema for a monday-style bigint id, accepted as string/number/bigint, kept as a string. */
export const bigIntIdSchema = z
    .union([z.string(), z.number().int().nonnegative(), z.bigint()])
    .transform((value) => normalizeId(value))
    .refine((value) => DECIMAL_ID.test(value), { message: 'expected a decimal bigint id' });
/** Zod schema for a board-scoped string id (column id / group id). */
export const boardScopedIdSchema = z
    .string()
    .trim()
    .refine((value) => BOARD_SCOPED_ID.test(value), {
    message: 'expected 1-20 characters of a-z, 0-9 or _',
});
function brandedBigIntId() {
    return bigIntIdSchema.transform((value) => value);
}
function brandedScopedId() {
    return boardScopedIdSchema.transform((value) => value);
}
export const boardIdSchema = brandedBigIntId();
export const itemIdSchema = brandedBigIntId();
export const userIdSchema = brandedBigIntId();
export const teamIdSchema = brandedBigIntId();
export const workspaceIdSchema = brandedBigIntId();
export const folderIdSchema = brandedBigIntId();
export const viewIdSchema = brandedBigIntId();
export const updateIdSchema = brandedBigIntId();
export const assetIdSchema = brandedBigIntId();
export const dashboardIdSchema = brandedBigIntId();
export const widgetIdSchema = brandedBigIntId();
export const docIdSchema = brandedBigIntId();
export const tagIdSchema = brandedBigIntId();
export const automationIdSchema = brandedBigIntId();
export const notificationIdSchema = brandedBigIntId();
export const webhookIdSchema = brandedBigIntId();
export const eventIdSchema = brandedBigIntId();
export const activityIdSchema = brandedBigIntId();
export const columnIdSchema = brandedScopedId();
export const groupIdSchema = brandedScopedId();
/** Unsafe cast used at the DB/API boundary once a value is known to be a valid id. */
export function asId(value) {
    return normalizeId(value);
}
export const asBoardId = (value) => asId(value);
export const asItemId = (value) => asId(value);
export const asUserId = (value) => asId(value);
export const asWorkspaceId = (value) => asId(value);
export const asColumnId = (value) => asId(value);
export const asGroupId = (value) => asId(value);
/**
 * Local id sequences start here so a generated id can never collide with a migrated monday id
 * (monday ids are ~10 digits today). doc 04 · Data model.
 */
export const LOCAL_ID_SEQUENCE_START = 1_000_000_000_000;
/** True when the id was minted by us rather than imported from monday. */
export function isLocalId(value) {
    const normalized = normalizeId(value);
    if (!DECIMAL_ID.test(normalized))
        return false;
    return BigInt(normalized) >= BigInt(LOCAL_ID_SEQUENCE_START);
}
//# sourceMappingURL=ids.js.map